import React, { useCallback, useEffect, useState } from "react";
import { dateObjectFromDateInputString } from "../../helpers/utilities";
import { CDKOrderImportSearchForm } from "./CDKOrderImportSearchForm";
import { CDKOrderImportTable } from "./CDKOrderImportTable";
import {
  getCDKOrdersByCustomerName,
  getCDKOrdersByInvoiceList,
  getCDKOrdersForDateRange,
  getCdkOrders,
} from "../../services/cdkOrderService";
import { codOptions, newPieceTypes } from "../../constants";
import handleApiError from "../../helpers/handleApiError";
import { useHistory } from "react-router-dom";
import { viewStates } from "../../constants";
import { defaultTitle } from "../../config";
import { Helmet } from "react-helmet";
import { format as dateFormat, add as dateAdd } from "date-fns";

import { getCDKShipViaDefault } from "../../services/cdkFieldPreferenceService";
import { getCDKCodDefaultValues } from "../../services/cdkOrderService";
// import { DilDropdown } from "../Utility/DilDropdown";
import { toast } from "react-toastify";
// import { TooltipInfoHover } from "../Utility/TooltipInfoHover";

/*
CDK RAW FIELDS EXAMPLE:

accountingAccount: "FMC-A"
billOfLading: null
closedDate: "2023-04-19T00:00:00"
comments: "CAC"
custNo: "6315"
customerAddress: "2817900291"
customerCSZ: ""
customerName: "CRAIG CLINTON"
customerName2: null
errorLevel: "0"
errorMessage: "9"
firstName: null
homePhone: null
hostItemId: "974789"
id: 13
invSaleType: "CREDIT"
invoiceNumber: "974789"
invoiceType: "Q"
isCompName: "BS"
lastName: "CRAIG CLINTON"
middleName: null
numberOfParts: 1
openDate: "2022-10-13T00:00:00"
poNumber: null
postedDate: "2023-04-19T00:00:00"
postedTime: null
printedTime: null
quoteID: null
referNo: null
shipDate: "2023-04-19T00:00:00"
shipToLine1: null
shipToLine2: null
shipToLine3: null
shipToLine4: null
shipVia: null
totalAirFreightChg: 0
totalFees: 0
totalFreightChg: 0
totalPartsCost: 0
totalPartsSale: 0
totalTax: 0
vehID: null
voidDate: "2023-04-19T00:00:00"
*/

/*
TABLE DATA:
PONumber????? : cdk.PONumber - might be useful to client for identification? Invoice number might take precedence.
CDKOrderID: CDK.id
CustomerName: {CDK.customerName - main
//maybe include these fields to show in the modal and allow client to build final customer name using them:
//CustomerName2
//firstName
//middleName
//lastName
}
CustomerNumber: CDK.custNo (same column as name)
CustomerAddress: {
    cdk.CustomerAddress - main
    cdk.CustomerCSZ - main
    cdk.shipToLine1
    cdk.shipToLine2
    cdk.shipToLine3
    cdk.shipToLine4
    cdk.shipVia???????? Zone 15/CO-OP
}
comments: cdk.comments
invoiceNumber: cdk.invoiceNumber
#Parts: cdk.NumberOfParts
#ShippingLabels: initialize to cdk.NumberOfParts or 0 or 1? 
PieceType: initialize to package?
DeclaredValue: initialize to cdk.totalPartsSale
CODType: figure out if there is a CDK field we can use to determine the initial state for this. If not we can have a setting for the client to set a default initial COD type for all their orders.
Time Code: Retrieve the time codes available to the order based on client and destination city, state, and zip. Maybe have a setting for client to order initial time code preference depending on what is available or "Let me decide for each order".
Insurance: Make setting for selecting default initial value.
originalData: Object containing all CDK Values in their original state.
}

//Since we are going with the 1 table design we can take out the cancel button, but maybe we can have a delete button in case the client no longer wants that order to be shown on their table ever.

*/
const defaultFromOffset = -6; //we are searching inclusively so for example, 7/4 to 7/10 seems like only 6 days but if you count it is actually 7.
//however the endpoint does not search inclusively, except for the orders on 7/10 12am per our example, so I add a day before I send the request.
const defaultToOffset = 0;
export const CDKOrderImport = ({ setLoading }) => {
  const [importedCDKOrdersTableData, setImportedCDKOrdersTableData] = useState(
    []
  );

  const history = useHistory();

  const [viewState, setViewState] = useState(viewStates.loading);

  // last element is always the ship via filter
  const filterByShipVia = (data, filter) => {
    if (filter.length === 0 || data.length === 0) return data;

    // we have to do this because the filter is an array of objects
    const filterValues = filter.map((filter) =>
      filter.filterName?.toLowerCase()
    );

    return data?.filter((row) => {
      // if the row ship via contains a null value and filter values contains "" then return true
      if (row?.shipVia === null && filterValues.includes("")) return true;

      // all filter values

      const rowShipVia = row?.shipVia?.toLowerCase() || "";

      if (filterValues.includes(rowShipVia)) return true;

      // Substring match
      const isSubstring = filterValues.some((filterValue) =>
        rowShipVia.includes(filterValue)
      );

      if (isSubstring) return true;

      if (
        filterValues.includes("coop") &&
        (rowShipVia.includes("co-op") ||
          rowShipVia.includes("coop") ||
          rowShipVia.includes("co op"))
      )
        return true;

      return false;
    });
  };

  // console.log(viewState);
  const processOrderData = useCallback((orders, filters, defaultCods) => {
    return filterByShipVia(
      //filter out by filter preferences first
      orders,
      filters
    )
      .filter((cdkOrder) => {
        return !cdkOrder.isImported; //filter out previously imported orders, (right now we don't do anything with them on the frontend)
      })
      .map((cdkOrder) => {
        //translate the data into a form that is basically like filling out an order.
        //we want to use the shipto fields (shipping address) and fallback to the old method (billing address at least for dhc14949) if those are empty.
        // Null Coalescing does NOT work for falsy values, e.g. it will return itself for empty strings. So use ternaries instead.
        const initialCustomerName = cdkOrder.shipToLine1
          ? cdkOrder.shipToLine1
          : cdkOrder.customerName
          ? cdkOrder.customerName
          : "";
        const initialCustomerAddress = cdkOrder.shipToLine2
          ? cdkOrder.shipToLine2
          : cdkOrder.customerAddress
          ? cdkOrder.customerAddress
          : "";
        // const cityStateZip = cdkOrder.customerCSZ ?? "";
        // const city = cityStateZip?.split(", ")?.[0] ?? "";
        // const stateAndZip = cityStateZip?.split(", ")?.[1] ?? "";
        // const state = stateAndZip?.split(" ")?.[0] ?? "";
        // const zip = stateAndZip?.split(" ")?.[1] ?? "";
        // console.log(cdkOrder.shipToLine3 ? cdkOrder.shipToLine3 : "Not found");
        const cityStateZip = cdkOrder.shipToLine3
          ? cdkOrder.shipToLine3
          : cdkOrder.customerCSZ
          ? cdkOrder.customerCSZ
          : "";
        const city = cityStateZip?.split(", ")?.[0] ?? "";
        const stateAndZip = cityStateZip?.split(", ")?.[1] ?? "";
        const state = stateAndZip?.split(" ")?.[0] ?? "";
        const zip = stateAndZip?.split(" ")?.[1] ?? "";
        return {
          CDKOrderID: cdkOrder.id,
          originalCDKData: cdkOrder,
          initialData: {
            customerName: initialCustomerName,
            customerNumber: cdkOrder.custNo,
            customerAddress: {
              address: initialCustomerAddress,
              // cityStateZip: cityStateZip,
              city: city,
              state: state,
              zip: zip,
              // shipToLine1: cdkOrder.shipToLine1,
              // shipToLine2: cdkOrder.shipToLine2,
              // shipToLine3: cdkOrder.shipToLine3,
              // shipToLine4: cdkOrder.shipToLine4,
              // shipVia: cdkOrder.shipVia,
            },
            comments: cdkOrder.comments ?? "",
            invoiceNumber: cdkOrder.invoiceNumber,
            CDKNumParts: cdkOrder.numberOfParts,
            // numShippingLabels: cdkOrder.numberOfParts,
            numShippingLabels: 1,
            pieceType: newPieceTypes.Package.value, // For CDK, for the pieceType "Piece" Carlos requested it say "Package" instead of "Piece", but as far as backend is concerned we still want to send over the value for "Piece" which is ""
            declaredValue: cdkOrder.totalPartsSale,
            // CODType: codOptions.None.value, // Figure out what to initialize this to. If there is no way to determine, make a default initial value setting
            CODType: defaultCods?.includes(cdkOrder.invSaleType)
              ? codOptions.COD.value
              : codOptions.None.value,

            timeCode: "", // Will have to get this from backend.
            insurance: false, // make setting for default initial value. There MIGHT be some CDK
          },
          dataToSubmit: {
            customerName: initialCustomerName,
            customerNumber: cdkOrder.custNo,
            customerAddress: {
              address: initialCustomerAddress,
              // cityStateZip: cityStateZip,
              city: city,
              state: state,
              zip: zip,
              // shipToLine1: cdkOrder.shipToLine1,
              // shipToLine2: cdkOrder.shipToLine2,
              // shipToLine3: cdkOrder.shipToLine3,
              // shipToLine4: cdkOrder.shipToLine4,
              // shipVia: cdkOrder.shipVia,
            },
            comments: cdkOrder.comments ?? "",
            invoiceNumber: cdkOrder.invoiceNumber,
            CDKNumParts: cdkOrder.numberOfParts,
            // numShippingLabels: cdkOrder.numberOfParts ?? 1,
            // pieceType: pieceTypes.Piece.value, // For CDK, for the pieceType "Piece" Carlos requested it say "Package" instead of "Piece", but as far as backend is concerned we still want to send over the value for "Piece" which is ""
            // declaredValue: cdkOrder.totalPartsSale,
            // CODType: codOptions.None.value, // Figure out what to initialize this to. If there is no way to determine, make a default initial value setting
            timeCode: "", // Will have to get this from backend.
            timeCodes: [], //Time code component retrieves this based on what is passed in customerAddress.city,
            didInitialTimeCodeFetch: false,
            // insurance: false, // make setting for default initial value. There MIGHT be some CDK field that we can use to determine the initial value for this, or we can decide on one.
            shipments: [
              {
                // numShippingLabels: cdkOrder.numberOfParts
                //   ? cdkOrder.numberOfParts
                //   : 1,
                numShippingLabels: 1,
                pieceType: newPieceTypes.Package.value,
                declaredValue: cdkOrder.totalPartsSale,
                comments: cdkOrder.comments ?? "",
                insurance: false,
                CODType: defaultCods?.includes(cdkOrder.invSaleType)
                  ? codOptions.COD.value
                  : codOptions.None.value,
              },
            ],
          },
          isImported: cdkOrder.isImported,
          isExpanded: false,
          isSelected: false,
          isHidden: cdkOrder.isHidden,
          wasSubmitted: false, //this is a field for dilitextcp. It is used to make dilitextcp turn red and show errors for an input that has not been touched but submission was attempted.
        };
      });
  }, []);
  const searchTypes = {
    dateRange: "DateRange",
    invoice: "Invoice",
    customer: "Customer",
    // customerNumber: "Customer Number",
  };
  const handleSearchForCDKOrders = useCallback(
    async (
      searchType,
      fromDate,
      toDate,
      selectedInvoices,
      selectedCustomers,
      isPageLoad = false
    ) => {
      // console.log(viewState);
      // console.log("----handleSearchForCDKOrders---------");
      // console.log(searchType);
      // console.log(fromDate);
      // console.log(toDate);
      // console.log(selectedInvoices);
      // console.log(selectedCustomers);
      // console.log(isPageLoad);
      // console.log("------------------------------");

      try {
        if (viewState === viewStates.loading && !isPageLoad) {
          toast.warn("Currently retrieving orders, please wait.");
          return;
        }
        setViewState(viewStates.loading);
        setImportedCDKOrdersTableData([]);

        const [importedCDKOrdersResponse, filtersResponse, defaultCODValues] =
          await Promise.all([
            searchType === searchTypes.dateRange
              ? getCDKOrdersForDateRange(
                  fromDate,
                  dateFormat(
                    dateAdd(dateObjectFromDateInputString(toDate), { days: 1 }),
                    "yyyy-MM-dd"
                  ) //add 1 day to keep the search inclusive
                )
              : searchType === searchTypes.invoice
              ? getCDKOrdersByInvoiceList(selectedInvoices)
              : searchType === searchTypes.customer
              ? getCDKOrdersByCustomerName(selectedCustomers, "", true)
              : searchType === searchTypes.customerNumber
              ? getCDKOrdersByCustomerName("", selectedCustomers, false)
              : null,
            getCDKShipViaDefault(),
            getCDKCodDefaultValues(),
          ]);

        const filterResponseVisible = filtersResponse.data.filter(
          (filter) => filter.isVisible
        );
        const importedCDKOrders = importedCDKOrdersResponse.data;
        // console.log(importedCDKOrders);

        const codDefaultActive = defaultCODValues.data
          .filter((filter) => filter.isActive)
          .map((filter) => filter.invSaleType);

        // console.log(importedCDKOrders);
        const translatedCDKOrdersToShow = processOrderData(
          importedCDKOrders,
          // dummyCDKData,
          filterResponseVisible,
          codDefaultActive
        );

        setImportedCDKOrdersTableData(translatedCDKOrdersToShow);

        setViewState(viewStates.success);
      } catch (ex) {
        if (ex?.response?.status === 404) {
          setViewState(viewStates.nodata);
          handleApiError(ex, history);
          return;
        } else {
          // console.log(ex);
          handleApiError(ex, history);
          setViewState(viewStates.error);
        }
      }
    },
    [viewState, history]
  );

  useEffect(() => {
    handleSearchForCDKOrders(
      searchTypes.dateRange,
      dateFormat(
        dateAdd(new Date(), { days: defaultFromOffset }),
        "yyyy-MM-dd"
      ),
      dateFormat(dateAdd(new Date(), { days: defaultToOffset }), "yyyy-MM-dd"),
      null,
      null,
      true
    );
  }, []);

  //dropdown component test stuff, "dt" - dropdowntest
  // const [dtOptions, setDTOptions] = useState([
  //   { display: "default", value: "" },
  //   { display: "This is option 1", value: "1" },
  //   { display: "This is option 2", value: "2" },
  //   { display: "This is option 3", value: "3" },
  //   { display: "This is option 4", value: "4" },
  //   { display: "This is option 5", value: "5" },
  //   { display: "This is option 6", value: "6" },
  // ]);
  // const [selectedDToption, setSelectedDTOption] = useState("");

  return (
    <>
      <Helmet>
        <title>CDK Order Import - {defaultTitle}</title>
      </Helmet>
      <div>
        <div className="text-center">
          <h1 className="mb-4">CDK Order Import</h1>
        </div>
        <CDKOrderImportSearchForm
          searchTypes={searchTypes}
          handleSubmitCDKSearch={handleSearchForCDKOrders}
          defaultFromOffset={defaultFromOffset}
          defaultToOffset={defaultToOffset}
        />
        {/* <DilDropdown
          options={dtOptions}
          selectedOption={selectedDToption}
          setSelectedOption={setSelectedDTOption}
          // customIcon={(props) => <BsChevronDown {...props} />}
        /> */}
        <div className="text-left text-note font-small px-3 py-2">
          Please Note: Currently only closed orders will be pulled.
        </div>
        <CDKOrderImportTable
          cdkOrderTableData={importedCDKOrdersTableData}
          setCDKOrderTableData={setImportedCDKOrdersTableData}
          viewState={viewState}
          setLoading={setLoading}
        />
      </div>
    </>
  );
};
